import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ObjectResponse } from '@verantos-dev/types-common';

import { authFetch } from './fetchers';
import { avKeys } from './queryKeys';
import type { AnalyticalVariableDetail } from './useAnalyticalVariableDetailQuery';
import type { AnalyticalVariableListItem } from './useAnalyticalVariableListQuery';

/**
 * Discriminator for which store the AV came from. ``library`` rows live
 * in ``ANALYTICAL_VARIABLE`` (cohort-builder authored, UUID-keyed,
 * full DSL ``jsonValue``). ``knowledge`` rows live in ``KNOWLEDGE``
 * (epi-study-agent authored, ``av.<slug>``-keyed, raw SQL body only).
 */
export type VariableDetailSource = 'library' | 'knowledge';

/**
 * Unified detail shape for AVs from either backing store.
 *
 * Superset of ``AnalyticalVariableDetail``: ``jsonValue`` is ``null`` for
 * knowledge-backed entries (no DSL serialisation today; they only
 * carry a templated SQL body). Callers that need the DSL (e.g. the
 * cohort-builder editor) must check ``source === 'library'`` first.
 *
 * The agent's session-scoped AVs are surfaced here so the project page
 * detail route can resolve them — without this branching, a chat-created
 * AV's id (``av.<slug>``) hits ``GET /analytical-variables/<id>`` and
 * 404s because that store is UUID-keyed.
 */
export interface VariableDetail extends AnalyticalVariableDetail {
  source: VariableDetailSource;
  /** Raw SQL body (knowledge-backed AVs only). */
  sqlBody: string | null;
  /** Output type from the KNOWLEDGE payload (e.g. ``boolean``, ``date``). */
  outputType: string | null;
  /** Concept-group ids referenced by the AV. Knowledge only. */
  conceptGroupIds: string[];
  /** Dataset schema the SQL was drafted against. Knowledge only. */
  datasetSchema: string | null;
  /**
   * Lifecycle status string.
   *
   * - Knowledge-backed: ``drafting`` / ``published`` (from the KNOWLEDGE
   *   row's payload) — drives the chip Drafting/Verified/Error for
   *   in-flight rows.
   * - Library-backed: the column STATUS (``curated`` / null today).
   *   NOT used by the chip resolver — library rows always render
   *   "Saved" regardless. Surfaced for future consumers.
   */
  status: string | null;
  /** AV slugs this AV references via ``{av:<slug>}`` placeholders. */
  sources: string[];
}

const KNOWLEDGE_AV_ID_PREFIX = 'av.';

/**
 * Knowledge ids are stamped ``av.<slug>`` by the orchestrator (see
 * ``record_av_artifact``). The library store uses opaque UUIDs which
 * never contain a dot, so prefix discrimination is unambiguous. Still
 * exported for surfaces that branch UI on id shape; the detail read
 * itself no longer classifies — the server resolves any ref.
 */
export const isKnowledgeAvId = (id: string): boolean => id.startsWith(KNOWLEDGE_AV_ID_PREFIX);

/**
 * Project the AV-list payload to ``VariableDetail`` so callers can
 * pre-warm the TanStack detail cache before navigating from the
 * artifact list. Workaround for a backend asymmetry: the list endpoint
 * returns ``av_denovo`` rows, but ``GET /analytical-variables/<id>``
 * 404s on those same UUIDs today, so the detail page would otherwise
 * show "Could not load variable." Pre-warming covers the gap until
 * the backend fix lands; the background refetch overwrites with fresh
 * data when the endpoint starts answering.
 *
 * Mirrors ``libraryDetailToVariableDetail`` for the fields the list
 * shape carries; the rest (``organizationId``, ``modifiedBy``,
 * concept-group / dataset-schema / status / sources) are nullable on
 * ``VariableDetail`` and absent from the list wire shape.
 */
export const libraryAvToVariableDetail = (item: AnalyticalVariableListItem): VariableDetail => ({
  source: 'library',
  analyticalVariableId: item.analyticalVariableId,
  organizationId: null,
  name: item.name,
  description: item.description,
  format: item.format,
  jsonValue: item.jsonValue,
  sqlBody: item.sqlBody,
  kind: item.kind,
  owner: item.owner,
  createdBy: item.createdBy,
  modifiedBy: undefined,
  outputType: item.format,
  conceptGroupIds: [],
  datasetSchema: null,
  // List shape may carry ``status`` (column STATUS) — pass through.
  // Chip resolver doesn't use this; library rows are always "Saved".
  status: item.status ?? null,
  sources: [],
});

/**
 * Status-chip vocabulary surfaced by the artifact card across page /
 * modal / drawer surfaces.
 *
 * - ``working`` — chat run is mid-stream; ephemeral until the agent
 *   finalizes the artifact.
 * - ``drafting`` — knowledge-backed AV not yet validated.
 * - ``verified`` — knowledge-backed AV with a passing validation
 *   outcome (manifest ``status === 'validated'``).
 * - ``error`` — validation failed; user action required before the
 *   AV is usable.
 * - ``saved`` — library-backed AV. The library tier infers "it works";
 *   no manifest signal modifies this label. Per OR-6025 product call.
 */
export type VariableStatusChip = 'working' | 'drafting' | 'verified' | 'error' | 'saved';

/**
 * Resolve the status chip for a variable surface.
 *
 * The chip is sourced primarily from ``detail.source`` (library vs.
 * knowledge) — the two tiers have different lifecycle vocabularies.
 * Mid-stream chat runs override everything else with "Working…".
 *
 * Library: always ``saved``. Per the OR-6025 product decision,
 * presence in the library implies "it works"; the column STATUS
 * (``curated`` vs. null) does not modify the chip.
 *
 * Knowledge: maps the manifest-internal status — the same enum the
 * chat stream's ``EpiArtifactStatus`` carries. Unknown / null falls
 * back to ``drafting``.
 */
export const resolveVariableStatusChip = (
  detail: Pick<VariableDetail, 'source' | 'status'> | null | undefined,
  options: { isStreaming?: boolean } = {}
): VariableStatusChip => {
  if (options.isStreaming) return 'working';
  if (!detail) return 'drafting';
  if (detail.source === 'library') return 'saved';
  // Knowledge-backed AV — manifest status drives the chip.
  switch (detail.status) {
    case 'validated':
      return 'verified';
    case 'blocked':
      return 'error';
    case 'drafting':
    case 'published':
    default:
      return 'drafting';
  }
};

/**
 * AV detail hook for the project page.
 *
 * Resolves any id shape — a library UUID, ``av.<slug>``, or a bare slug —
 * through a single endpoint, ``GET /analytical-variables/by-ref``. The
 * server reads the persisted ``ANALYTICAL_VARIABLE`` library row first,
 * then the live DynamoDB in-flight draft for the active chat session;
 * the KNOWLEDGE search index is never on the detail-load path. The
 * endpoint returns the unified ``VariableDetail`` wire shape directly, so
 * this hook is a thin pass-through (the backend owns the projection).
 *
 * ``chatId`` is the active chat/session id (the in-flight draft's DDB
 * partition key). Pass it from surfaces that have an open chat so a
 * still-drafting AV resolves on a hard refresh; omit it for context-free
 * (UUID, library) reads, which resolve from Snowflake alone.
 *
 * ``projectId`` (OR-6081 AC22) gates the Snowflake lookup by the project +
 * scope visibility rule — a project-scoped AV owned by a sibling project
 * resolves to 404, matching the library list.
 *
 * ``options.includeDeleted`` (OR-6234) asks the server to also return a
 * soft-deleted variable, and files the answer under ``avKeys.deletedDetail``
 * rather than ``libraryDetail`` so the variable's own page can never pick it
 * up from cache. Only the verification-result preview sets it.
 */
export const useVariableDetail = (
  analyticalVariableId: string | null,
  projectId?: string | null,
  chatId?: string | null,
  options?: { includeDeleted?: boolean }
): UseQueryResult<ObjectResponse<VariableDetail>> => {
  const includeDeleted = options?.includeDeleted === true;
  return useQuery({
    queryKey: includeDeleted
      ? avKeys.deletedDetail(analyticalVariableId ?? '', projectId ?? '')
      : avKeys.libraryDetail(analyticalVariableId ?? '', projectId ?? ''),
    queryFn: async (): Promise<ObjectResponse<VariableDetail>> => {
      if (!analyticalVariableId) {
        throw new Error('analyticalVariableId is required');
      }
      const params = new URLSearchParams({ ref: analyticalVariableId });
      if (chatId) params.set('chat', chatId);
      if (projectId) params.set('projectId', projectId);
      if (includeDeleted) params.set('includeDeleted', 'true');
      return authFetch<ObjectResponse<VariableDetail>>(
        `/analytical-variables/by-ref?${params.toString()}`
      );
    },
    enabled: !!analyticalVariableId,
  });
};
